import { useState, useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { Play } from 'lucide-react'
import { OrgChartService, Team, TeamMember, RoleWithMember } from '../services/orgChartService'
import {
  LiveWorkService,
  LiveWorkStatus,
  aucklandDayKey,
} from '../services/liveWorkService'
import { useAuth } from '../contexts/AuthContext'
import { LoadingSpinner, ErrorAlert, HolidayBanner } from '../components'
import { LiveWorkCard } from '../components/LiveWorkCard'
import { WeekHoursRow } from '../components/WeekHoursRow'
import { aucklandWeekDayKeys } from '../utils/weekHours'
import { loadMemberWeekHours } from '../services/weekHoursService'
import { fetchTodaysThanksSplash } from '../services/thanksSplashService'

export default function CarpeDiem() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [liveStatuses, setLiveStatuses] = useState<LiveWorkStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  /** Team Brevity member id → Mon–Sun hours. */
  const [weekHoursByMemberId, setWeekHoursByMemberId] = useState<Map<string, number[]>>(
    () => new Map()
  )
  const [thanksSplashUrl, setThanksSplashUrl] = useState<string | null>(null)

  const { firebaseUser, isAdmin } = useAuth()
  const orgChartService = useMemo(() => OrgChartService.getInstance(), [])
  const liveWorkService = useMemo(() => LiveWorkService.getInstance(), [])
  const unsubRef = useRef<(() => void) | null>(null)

  const [teams, setTeams] = useState<Team[]>([])
  const [rolesWithMembers, setRolesWithMembers] = useState<RoleWithMember[]>([])
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set())
  const [showTimeBlocks, setShowTimeBlocks] = useState(false)

  const dayKey = aucklandDayKey()
  const weekDayKeys = useMemo(() => aucklandWeekDayKeys(), [dayKey])

  const memberTeamMap = useMemo(() => {
    const map = new Map<string, Set<string>>()

    rolesWithMembers.forEach((role) => {
      if (!role.assignedMember) return

      const email = role.assignedMember.primaryEmail.toLowerCase()
      const effectiveTeams =
        role.teams && role.teams.length > 0 ? role.teams : role.inheritedTeams || []

      if (!map.has(email)) {
        map.set(email, new Set())
      }
      effectiveTeams.forEach((teamName) => map.get(email)!.add(teamName))
    })

    return map
  }, [rolesWithMembers])

  const selectedTeamNames = useMemo(() => {
    return new Set(teams.filter((t) => selectedTeamIds.has(t.id)).map((t) => t.name))
  }, [teams, selectedTeamIds])

  const filteredStatuses = useMemo(() => {
    if (selectedTeamIds.size === 0) return liveStatuses

    return liveStatuses.filter((status) => {
      const memberTeams = memberTeamMap.get(status.email.toLowerCase())
      if (!memberTeams) return false
      for (const teamName of selectedTeamNames) {
        if (memberTeams.has(teamName)) return true
      }
      return false
    })
  }, [liveStatuses, selectedTeamIds, selectedTeamNames, memberTeamMap])

  const toggleTeamFilter = (teamId: string) => {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev)
      if (next.has(teamId)) next.delete(teamId)
      else next.add(teamId)
      return next
    })
  }

  const clearTeamFilters = () => setSelectedTeamIds(new Set())

  useEffect(() => {
    if (!firebaseUser) {
      setLoading(false)
      return
    }

    let cancelled = false

    const start = async () => {
      try {
        setLoading(true)
        setError('')
        unsubRef.current?.()
        unsubRef.current = null

        const [members, emailToUid] = await Promise.all([
          liveWorkService.getVisibleMembers(),
          liveWorkService.getEmailToUidMap(),
        ])

        if (cancelled) return

        setTeamMembers(members)
        unsubRef.current = liveWorkService.subscribeTeamLiveWork(
          members,
          emailToUid,
          dayKey,
          (statuses) => {
            if (!cancelled) setLiveStatuses(statuses)
          }
        )
      } catch (err: unknown) {
        console.error('Error loading live work board:', err)
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unknown error'
          setError(`Failed to load live work: ${message}`)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    start()

    return () => {
      cancelled = true
      unsubRef.current?.()
      unsubRef.current = null
    }
  }, [firebaseUser, dayKey, liveWorkService])

  // Shared daily thanks splash for Done-for-today decks
  useEffect(() => {
    if (!firebaseUser) {
      setThanksSplashUrl(null)
      return
    }
    let cancelled = false
    void fetchTodaysThanksSplash().then((splash) => {
      if (!cancelled) setThanksSplashUrl(splash?.imageUrl ?? null)
    })
    return () => {
      cancelled = true
    }
  }, [firebaseUser, dayKey])

  // Load Mon–Sun PWF hours only when an admin has time blocks visible
  useEffect(() => {
    if (!firebaseUser || !isAdmin || !showTimeBlocks || teamMembers.length === 0) {
      setWeekHoursByMemberId(new Map())
      return
    }

    let cancelled = false

    const loadWeekHours = async () => {
      try {
        const byMemberId = await loadMemberWeekHours(teamMembers, weekDayKeys)
        if (!cancelled) setWeekHoursByMemberId(byMemberId)
      } catch (err) {
        console.error('Error loading weekly PWF hours:', err)
        if (!cancelled) setWeekHoursByMemberId(new Map())
      }
    }

    loadWeekHours()
    return () => {
      cancelled = true
    }
  }, [firebaseUser, isAdmin, showTimeBlocks, weekDayKeys, teamMembers])

  useEffect(() => {
    const loadTeamsAndRoles = async () => {
      try {
        const [teamsData, rolesData] = await Promise.all([
          orgChartService.getAllTeams(),
          orgChartService.getRolesWithMembers(),
        ])
        setTeams(teamsData)
        setRolesWithMembers(rolesData)
      } catch (err) {
        console.error('Error loading teams for filtering:', err)
      }
    }
    loadTeamsAndRoles()
  }, [orgChartService])

  const todayLabel = new Date().toLocaleDateString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <LoadingSpinner message="Loading mindful working board..." fullPage />
        ) : error ? (
          <ErrorAlert
            title="Unable to Load Live Work"
            message={error}
            onRetry={() => window.location.reload()}
            fullPage
          />
        ) : teamMembers.length === 0 ? (
          <ErrorAlert
            title="No Team Members"
            message="No visible people in Team Brevity yet. Sync the directory there, and make sure leavers are marked hidden."
            variant="info"
            onRetry={() => window.history.back()}
            retryText="Go to Hub"
            fullPage
          />
        ) : (
          <>
            <div className="mb-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center space-x-3 min-w-0">
                  <Play className="h-6 w-6 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className="text-2xl font-bold text-gray-900">Carpe Diem</h1>

                      {teams.length > 0 && (
                        <>
                          <div className="h-5 w-px bg-gray-300" />
                          <div className="flex items-center gap-2 flex-wrap">
                            {teams.map((team) => {
                              const isSelected = selectedTeamIds.has(team.id)
                              const memberCount = teamMembers.filter((member) => {
                                const memberTeams = memberTeamMap.get(
                                  member.primaryEmail.toLowerCase()
                                )
                                return memberTeams?.has(team.name)
                              }).length

                              return (
                                <button
                                  key={team.id}
                                  onClick={() => toggleTeamFilter(team.id)}
                                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                    isSelected
                                      ? 'ring-2 ring-offset-1'
                                      : 'opacity-50 hover:opacity-100'
                                  }`}
                                  style={
                                    {
                                      backgroundColor: isSelected
                                        ? `${team.color}25`
                                        : `${team.color}10`,
                                      color: team.color,
                                      '--tw-ring-color': isSelected ? team.color : 'transparent',
                                    } as CSSProperties
                                  }
                                  title={`${team.name}: ${memberCount} member${memberCount !== 1 ? 's' : ''}`}
                                >
                                  <span
                                    className="w-2 h-2 rounded-full mr-1.5"
                                    style={{ backgroundColor: team.color }}
                                  />
                                  {team.name}
                                </button>
                              )
                            })}
                            {selectedTeamIds.size > 0 && (
                              <button
                                onClick={clearTeamFilters}
                                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <p className="text-gray-600">
                      {filteredStatuses.length === liveStatuses.length
                        ? `${liveStatuses.length} team member${liveStatuses.length !== 1 ? 's' : ''}`
                        : `${filteredStatuses.length} of ${liveStatuses.length} team members`}{' '}
                      • Live · {todayLabel}
                    </p>
                  </div>
                </div>

                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowTimeBlocks((v) => !v)}
                    className="flex-shrink-0 inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    {showTimeBlocks ? 'Hide Time Blocks' : 'Show Time Blocks'}
                  </button>
                )}
              </div>

              {selectedTeamIds.size > 0 && filteredStatuses.length === 0 && (
                <p className="mt-2 text-sm text-amber-600">
                  No team members found for selected teams. They may not have roles assigned yet.
                </p>
              )}
            </div>

            <HolidayBanner />

            <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {filteredStatuses.map((status) => {
                const hours = weekHoursByMemberId.get(status.memberId) ?? [
                  0, 0, 0, 0, 0, 0, 0,
                ]
                return (
                  <div key={status.memberId} className="flex flex-col gap-2 min-w-0">
                    <LiveWorkCard status={status} thanksSplashUrl={thanksSplashUrl} />
                    {isAdmin && showTimeBlocks && (
                      <WeekHoursRow hours={hours} dayKeys={weekDayKeys} />
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
